import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Dashboard from './admin/Dashboard';
import DepartmentManagement from './admin/DepartmentManagement';
import CourseManagement from './admin/CourseManagement';
import TestManagement from './admin/TestManagement';
import UserManagement from './admin/UserManagement';
import CouponManagement from './admin/CouponManagement';
import Analytics from './admin/Analytics';
import TestimonialManagement from './admin/TestimonialManagement';
import LegalManagement from './admin/LegalManagement';
import Settings from './admin/Settings';
import DeviceApproval from './admin/DeviceApproval';
import { LayoutDashboard, BookOpen, FileText, Users, BarChart3, MessageSquare, Shield, Settings as SettingsIcon, Menu, X, Building2, Tag, Smartphone } from 'lucide-react';

const AdminDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingDeviceCount, setPendingDeviceCount] = useState(0);

  // Fetch pending device requests count
  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const response = await axios.get('/admin/device-requests?status=pending');
        setPendingDeviceCount(response.data.requests?.length || 0);
      } catch (error) {
        console.error('Failed to fetch pending device requests:', error);
      }
    };

    fetchPendingCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'departments', label: 'Departments', icon: Building2 },
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'tests', label: 'Tests & Questions', icon: FileText },
    { id: 'coupons', label: 'Coupons', icon: Tag },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'devices', label: 'Device Approval', icon: Smartphone, badge: pendingDeviceCount },
    { id: 'testimonials', label: 'Testimonials', icon: MessageSquare },
    { id: 'legal', label: 'Legal Documents', icon: Shield },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: SettingsIcon },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard onNavigate={setActiveTab} />;
      case 'departments':
        return <DepartmentManagement />;
      case 'courses':
        return <CourseManagement />;
      case 'tests':
        return <TestManagement />;
      case 'coupons':
        return <CouponManagement />;
      case 'users':
        return <UserManagement />;
      case 'devices':
        return <DeviceApproval />;
      case 'testimonials':
        return <TestimonialManagement />;
      case 'legal':
        return <LegalManagement />;
      case 'analytics':
        return <Analytics />;
      case 'settings':
        return <Settings />;
      default:
        return <Dashboard onNavigate={setActiveTab} />;
    }
  };

  const handleMenuClick = (tabId) => {
    setActiveTab(tabId);
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Mobile menu button */}
              <button 
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              
              <img 
                src="https://customer-assets.emergentagent.com/job_cgda-exam-prep/artifacts/bek6ttpm_logo.png" 
                alt="Departmental Exam"
                className="h-10 sm:h-14 md:h-16 object-contain"
              />
            </div>
            <Button onClick={onLogout} variant="outline" size="sm" className="text-xs sm:text-sm">
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="flex relative">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-64 bg-white min-h-screen border-r">
          <nav className="p-4 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  data-testid={`nav-${item.id}`}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-blue-100 text-blue-700 font-semibold'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${isActive ? 'text-blue-700' : 'text-gray-500'}`} />
                    <span>{item.label}</span>
                  </div>
                  {item.badge > 0 && (
                    <Badge variant="destructive" className="ml-2 h-5 min-w-5 flex items-center justify-center text-xs">
                      {item.badge}
                    </Badge>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Mobile Sidebar */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-40 top-[57px] sm:top-[65px]">
            <div className="absolute inset-0 bg-gray-900 bg-opacity-50" onClick={() => setMobileMenuOpen(false)} />
            <aside className="absolute left-0 w-64 bg-white h-full shadow-xl">
              <nav className="p-4 space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleMenuClick(item.id)}
                      data-testid={`mobile-nav-${item.id}`}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
                        isActive
                          ? 'bg-blue-100 text-blue-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className={`w-5 h-5 ${isActive ? 'text-blue-700' : 'text-gray-500'}`} />
                        <span>{item.label}</span>
                      </div>
                      {item.badge > 0 && (
                        <Badge variant="destructive" className="ml-2 h-5 min-w-5 flex items-center justify-center text-xs">
                          {item.badge}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </nav>
            </aside>
          </div>
        )}

        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;